import React, { Fragment, memo, useState } from "react";
import TopicAiField from "./TopicAiField";

function safeUrl(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}

function ImageDownload({ url }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const href = safeUrl(url);
  if (!href) return null;
  const download = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        `/api/download-image?url=${encodeURIComponent(href)}`,
      );
      if (
        !response.ok ||
        !response.headers.get("content-type")?.startsWith("image/")
      )
        throw new Error("Download failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        new URL(href).pathname.split("/").pop() || "topic-image.jpg";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      setError("Image download failed. Please retry or open the image.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="image-actions">
      <button type="button" onClick={download} disabled={busy}>
        {busy ? "Downloading…" : "Download image"}
      </button>
      <a href={href} target="_blank" rel="noopener noreferrer">
        Open image
      </a>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

function Fields({ rows }) {
  return (
    <table className="topic-fields">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>
              {value === null || value === undefined || value === ""
                ? "—"
                : value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TopicImage({ image, heading }) {
  const url = safeUrl(image.image);
  return (
    <section className="topic-image-detail">
      <h4>{heading}</h4>
      {url && (
        <img
          src={url}
          alt={image.caption || image.title || ""}
          loading="lazy"
          decoding="async"
          width="480"
          height="150"
        />
      )}
      <Fields
        rows={[
          // ["Image ID", image.id],
          // ["Title", image.title],
          // ["Slug", image.slug],
          ["Caption", image.caption],
          ["Keywords", image.keywords],
          [
            "Image URL",
            url ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {url}
              </a>
            ) : null,
          ],
        ]}
      />
      <ImageDownload url={url} />
    </section>
  );
}

const TopicRow = memo(function TopicRow({
  topic,
  Time,
  isExpanded,
  setExpanded,
}) {
  return (
    <Fragment key={topic.id}>
      <tr>
        <td>
          <strong>{topic.title}</strong>
          {/* <small>ID: {topic.id}</small> */}
        </td>
        <td>{topic.byline || "—"}</td>
        <td>
          <div className="topic-tags">
            {(topic.tags || []).map((tag) => (
              <span key={tag.id}>{tag.name}</span>
            ))}
          </div>
        </td>
        <td>
          <Time value={topic.created_at} />
        </td>
        <td>
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={`topic-${topic.id}`}
            onClick={() =>
              setExpanded((current) => (current === topic.id ? null : topic.id))
            }
          >
            {isExpanded ? "Hide" : "View"} details
          </button>
        </td>
      </tr>
      <tr hidden={!isExpanded} id={`topic-${topic.id}`}>
        <td colSpan={5} className="topic-expanded">
          {isExpanded && (
            <div>
              {/* <h3>Topic details</h3> */}
              <Fields
                rows={[
                  ["Title", <TopicAiField value={topic.title} />],
                  ["Slug", <TopicAiField field="slug" value={topic.slug?.replace(/--\d+$/, "")} topicTitle={topic.title} />],
                  // ["Author", topic.byline],
                  ["Language", topic.language],
                  ["Description", <TopicAiField field="description" value={topic.short_desc} topicTitle={topic.title} />],
                  ["Published", <Time value={topic.created_at} />],
                  ["Updated", <Time value={topic.updated_at} />],
                  ["Image caption", topic.image_caption],
                  [
                    "Video",
                    safeUrl(topic.video_url) ? (
                      <a
                        href={safeUrl(topic.video_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open video
                      </a>
                    ) : null,
                  ],
                ]}
              />
              <h3>Tags</h3>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "5px",
                  marginBottom: "8px",
                }}
              >
                {(topic.tags || []).map((tag) => (
                  <div
                    key={tag.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "3px 7px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      background: "#f8f9fa",
                      fontSize: "12px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "600",
                      }}
                    >
                      {tag.hindi_name || "—"}
                    </span>

                    <span
                      style={{
                        color: "#6b7280",
                      }}
                    >
                      {tag.slug || "—"}
                    </span>
                  </div>
                ))}

                {!topic.tags?.length && <span>—</span>}
              </div>
              <h3>Source</h3>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "5px",
                  marginBottom: "8px",
                }}
              >
                {(topic.website || []).map((site) => (
                  <div
                    key={site.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "3px 7px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      background: "#f8f9fa",
                      fontSize: "12px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "600",
                      }}
                    >
                      {site.name || "—"}
                    </span>

                    <span
                      style={{
                        color: "#6b7280",
                      }}
                    >
                      {site.description || "—"}
                    </span>
                  </div>
                ))}

                {!topic.website?.length && <span>—</span>}
              </div>
              <h3>Images</h3>
              <div className="topic-images">
                {topic.image && (
                  <TopicImage
                    heading="Main image"
                    image={{
                      image: topic.image,
                      caption: topic.image_caption,
                    }}
                  />
                )}
                {topic.thumbnail && (
                  <TopicImage
                    heading="Thumbnail"
                    image={{
                      image: topic.thumbnail,
                      caption: topic.image_caption,
                    }}
                  />
                )}
                {(topic.images || []).map((image, index) => (
                  <TopicImage
                    key={image.id || index}
                    heading={`Gallery image ${index + 1}`}
                    image={image}
                  />
                ))}
              </div>
              {!topic.image && !topic.thumbnail && !topic.images?.length && (
                <p>No images available.</p>
              )}
            </div>
          )}
        </td>
      </tr>
    </Fragment>
  );
});

export default function TopicTable({ topics, Time }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="topic-table-scroll">
      <table className="topics-table">
        <caption className="visually-hidden">
          News topics with expandable details
        </caption>
        <thead>
          <tr>
            <th scope="col">Topic</th>
            <th scope="col">Author</th>
            <th scope="col">Categories</th>
            <th scope="col">Published</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {topics.map((topic) => (
            <TopicRow
              key={topic.id}
              topic={topic}
              Time={Time}
              isExpanded={expanded === topic.id}
              setExpanded={setExpanded}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
